import {Option, type FormControl} from 'platform/components';

import {useWatch} from 'react-hook-form';

import {pipe, prop, uniqBy} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetMakeModelWithMakeQuery, useGetMakeModelWithMakesQuery} from '@dms/api/sales';
import {VehicleModel} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useVehicleCatalogue} from '@dms/shared';

import {buildVehicleLookupMaps} from '../../../utils/buildVehicleLookupMaps';
import type {AssignmentConditionFormValues} from '../schemas/assignmentConditionFormSchema';

type ModelOptionsGroup = {
  label: string;
  options: readonly Option[];
};

export function useAssignmentConditionVehicleOptions(
  control: FormControl<AssignmentConditionFormValues>
) {
  const [vehicleType, selectedMakes] = useWatch({
    control,
    name: ['type', 'make'],
  });

  const hasVehicleType = isNotNil(vehicleType) && vehicleType !== '';
  const hasMakes = Array.isArray(selectedMakes) && selectedMakes.length > 0;

  const [vehicleCatalogue, {isLoading: isFuelLoading}] = useVehicleCatalogue(
    hasVehicleType ? (vehicleType ?? undefined) : undefined
  );
  const fuelOptions: Option[] =
    hasVehicleType && vehicleCatalogue.fuelTypeOptions
      ? vehicleCatalogue.fuelTypeOptions.map((opt) => ({value: opt.value, label: opt.label}))
      : [];

  const {data: makesResponse, isLoading: isMakesLoading} = useGetMakeModelWithMakeQuery(
    {
      vehicleType: vehicleType ?? '',
      lang: [i18n.language],
    },
    {skip: !hasVehicleType}
  );

  const {data: makesWithModelsResponse, isLoading: isModelsLoading} = useGetMakeModelWithMakesQuery(
    {
      vehicleType: vehicleType ?? '',
      makes: selectedMakes ?? [],
      lang: [i18n.language],
    },
    {skip: !hasVehicleType || !hasMakes}
  );

  const makeOptions: Option[] =
    makesResponse?.map((make) => ({
      label: make.default_label,
      value: make.make,
    })) ?? [];

  const modelOptionsGroupedByMake: ModelOptionsGroup[] =
    makesWithModelsResponse
      ?.map((make) => ({
        label: make.default_label,
        options: (make.models ?? []).map((model) => ({
          label: model.default_label,
          value: model.model,
        })),
      }))
      .filter((group) => group.options.length > 0) ?? [];

  const modelFamilyOptionsGroupedByMake: ModelOptionsGroup[] =
    makesWithModelsResponse
      ?.map((make) => ({
        label: make.default_label,
        options: pipe(
          (models: VehicleModel[]) => models.map((model) => model.model_group),
          (groups) => groups.filter(isNotNil),
          uniqBy(prop('group'))
        )(make.models ?? []).map((group) => ({
          label: group.default_label,
          value: group.group,
        })),
      }))
      .filter((group) => group.options.length > 0) ?? [];

  const getVehicleLookupMaps = () => buildVehicleLookupMaps(makesWithModelsResponse);

  const isMakeDisabled = !hasVehicleType;
  const isModelAndModelFamilyDisabled = !hasVehicleType || !hasMakes;
  const isFuelDisabled = !hasVehicleType;

  return {
    makeOptions,
    modelOptionsGroupedByMake,
    getVehicleLookupMaps,
    modelFamilyOptionsGroupedByMake,
    fuelOptions,
    isMakeModelLoading: isMakesLoading || isModelsLoading,
    isFuelLoading,
    isMakeDisabled,
    isModelDisabled: isModelAndModelFamilyDisabled,
    isModelFamilyDisabled: isModelAndModelFamilyDisabled,
    isFuelDisabled,
  };
}
